import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "@/lib/api";

const SiteContentContext = createContext(null);

// Strip "/api" suffix to get the asset origin (e.g. "https://starlink-mallorca.de").
const ASSET_ORIGIN = API_BASE.replace(/\/api$/, "");

// Resolve relative /api/uploads/... URLs to absolute (so they work everywhere).
export const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${ASSET_ORIGIN}${url}`;
  return url;
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/content");
      setContent(data);
    } catch (e) {
      // Public endpoint shouldn't fail, but degrade gracefully.
      setContent({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = load;
  const update = (next) => setContent(next);

  return (
    <SiteContentContext.Provider value={{ content, loading, refresh, update }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be inside SiteContentProvider");
  return ctx;
};

// Helper: pick localized value with locale fallback.
export const pickLocalized = (obj, locale, fallback = "") => {
  if (!obj || typeof obj !== "object") return fallback;
  return obj[locale] ?? obj.de ?? obj.en ?? fallback;
};
